//src/pages/CoreWord.tsx
import React, { useState, useEffect } from 'react';
import { Star, ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { RadioCard } from '../components/RadioCard';
import { cn } from '../utils/cn';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { CoreWord as CoreWordType } from '../types';
import { ProtectedLayout } from '../components/ProtectedLayout';

const predefinedWords: CoreWordType[] = ['Love', 'Joy', 'Happiness', 'Excitement', 'Freedom'];

export function CoreWord() {
  const navigate = useNavigate();
  const [selectedWord, setSelectedWord] = useState<CoreWordType>('');
  const [customWord, setCustomWord] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [intentions, setIntentions] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchIntentions() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data, error } = await supabase
            .from('impact_intentions')
            .select('intention_type')
            .eq('user_id', user.id);

          if (error) throw error;
          
          if (data) {
            setIntentions(data.map(i => i.intention_type));
          }
        }
      } catch (error) {
        console.error('Error fetching intentions:', error);
      }
    }

    fetchIntentions();
  }, []);

  const formatIntentions = (intentions: string[]) => {
    if (intentions.length === 0) return '';
    if (intentions.length === 1) return intentions[0];
    if (intentions.length === 2) return `${intentions[0]} and ${intentions[1]}`;
    return `${intentions.slice(0, -1).join(', ')}, and ${intentions[intentions.length - 1]}`;
  };

  const handleNext = async () => {
    if (isSubmitting) return;
    
    const wordToSave = isCustom ? customWord.trim() : selectedWord;
    if (!wordToSave) return;

    setIsSubmitting(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update the user's profile with their core word
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ core_word: wordToSave })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Navigate to core values page
      navigate('/core-values');
    } catch (error: any) {
      console.error('Error saving core word:', error);
      setError(error.message || 'Failed to save your core word');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
        <ProtectedLayout>
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center gap-3 mb-6">
          <Star className="w-8 h-8 text-[#236caf]" />
          <h1 className="text-3xl font-bold text-[#004060]">Your Core Word</h1>
        </div>

        <p className="text-xl text-gray-600 mb-8">
          Think of a time when you felt {formatIntentions(intentions)}. What best describes that feeling?
        </p>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {predefinedWords.map((word) => (
            <RadioCard
              key={word}
              label={word}
              selected={!isCustom && selectedWord === word}
              onClick={() => {
                setSelectedWord(word);
                setIsCustom(false);
                setCustomWord('');
              }}
            />
          ))}
          <div
            className={cn(
              'p-4 rounded-lg border-2 cursor-pointer transition-all duration-200',
              isCustom ? 'border-[#236caf] bg-blue-50' : 'border-gray-200 hover:border-[#236caf] hover:bg-blue-50/50'
            )}
            onClick={() => setIsCustom(true)}
          >
            <div className="flex flex-col gap-2">
              <span className="text-lg font-medium text-gray-700">Other</span>
              <input
                type="text"
                value={customWord}
                onChange={(e) => {
                  setCustomWord(e.target.value);
                  setIsCustom(true);
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Enter your word"
                className={cn(
                  'w-full p-2 rounded border transition-colors duration-200',
                  isCustom ? 'border-[#236caf]' : 'border-gray-200'
                )}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/impact-intentions')}
            className="w-1/2 flex items-center gap-2 justify-center"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>

          <Button
            onClick={handleNext}
            disabled={isSubmitting || (!selectedWord && !customWord.trim())}
            className={cn(
              "w-1/2 justify-center",
              (isSubmitting || (!selectedWord && !customWord.trim())) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
        </ProtectedLayout>
  );
}